<script>
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { params, url } from "@roxi/routify";
    import { PreferencesStore } from "$/stores/global/PreferencesStore";
    import Extension from "$/Extension.svelte";
    import { getElementAndTextNode } from "$/utils/CustomLocateV4/marker/utils";
    import { ExtensionStore } from "$/stores/ExtensionStore";
    import { AppStore } from "$/stores/AppStore";
    import { onDestroy, onMount } from "svelte";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { LoginService } from "$/services/LoginService";
    import {
        clearUrlByMarker,
        getRole,
        getRootDocument,
        isStringUUID,
        isPositiveWholeNumber,
        removeUrlParameters,
        showtToastMessage,
    } from "$/utils/Utils";
    import * as pdfjsLib from "pdfjs-dist";
    import * as pdfjsViewer from "pdfjs-dist/legacy/web/pdf_viewer";
    import LoadingMessage from "$/pages/_templates/LoadingMessage.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import { DocumentStore } from "$/stores/DocumentStore";
    import ToolbarBackButton from "$/pages/pdf/_components/ToolbarBackButton.svelte";
    import ErrorImg from "$/images/error/Error-404.png";
    import { TopicService } from "$/services/TopicService";
    import { StoreStatus } from "$/models/StoreStatus";

    pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdfjs/pdf.worker.min.mjs`;

    let urlParams = new URLSearchParams(window.location.hash.substring(1));
    let viewOnly = urlParams.get("viewOnly")?.split("#")[0] ?? false;
    let scale = 1;
    let pdfDocument = null;
    let eventBus = new pdfjsViewer.EventBus();
    let linkService = new pdfjsViewer.PDFLinkService({ eventBus });
    let pdfFindController = new pdfjsViewer.PDFFindController({
        eventBus,
        linkService,
    });
    let pdfContainer;
    let pdfViewer;
    let pagesInit = false;
    let highlightEditors = [];
    let isSelecting = false;
    let pointerClicked = false;
    let snippetToLocate = null;
    let userRole = null;
    let topicId;
    let pdfNotFound = false;
    let pdfPageCount = 0;
    let pdfDocName = pdfjsLib.getPdfFilenameFromUrl(
        decodeURIComponent($params.pdfLink),
    );
    let currentPageNumber = 1;
    let toastDiv = null;
    let pdfViewerContainer = null;
    let unsubscribeExtensionStore = null;
    let searchQuery = "";
    let searchFoundCount = 0;
    let searchCurrentPosition = 1;
    let searchLoading = false;
    let noSearchResult = false;
    let debounceSearchTimer;
    let typing = false;
    let extensionLoaded = false;
    let highlightSnippetIdInExtension = new URLSearchParams(
        window.location.hash.substring(1),
    ).get(ServiceConsts.SNIPPET_PARAM_NAME);

    const highlighterColors =
        "yellow=#FFFF98,green=#B4FF9E,blue=#ADD7FF,purple=#CFC2FF,red=#FF89B3,orange=#FFC58D";

    $: $PreferencesStore?.showAllHighlights, toggleAllSnippetsHighlight();
    $: $params, highlightAndLocateSnippet();

    onMount(() => {
        initPDF();
        getRootDocument("web").addEventListener(
            "pointerdown",
            pointerDownHandler,
        );
        getRootDocument("web").addEventListener(
            "selectionchange",
            selectionChangeHandler,
        );
        getRootDocument("web").addEventListener(
            "pointermove",
            pointerMoveHandler,
        );
        getRootDocument("web").addEventListener("pointerup", pointerUpHandler);
    });

    onDestroy(() => {
        unsubscribeTopicStore();
        getRootDocument("web").removeEventListener(
            "pointerdown",
            pointerDownHandler,
        );
        getRootDocument("web").removeEventListener(
            "selectionchange",
            selectionChangeHandler,
        );
        getRootDocument("web").removeEventListener(
            "pointermove",
            pointerMoveHandler,
        );
        getRootDocument("web").removeEventListener(
            "pointerup",
            pointerUpHandler,
        );
    });

    const fetchSnippetToLocate = async () => {
        let snippetIdToLocate = new URLSearchParams(
            window.location.hash.substring(1),
        ).get(ServiceConsts.SNIPPET_PARAM_NAME);
        if (snippetIdToLocate && !snippetToLocate)
            snippetToLocate =
                await ActiveTopicStore?.getSnippet(snippetIdToLocate);
    };

    const loadPDF = async (documentUrl) => {
        pdfNotFound = false;
        if (unsubscribeExtensionStore) unsubscribeExtensionStore();
        await fetchSnippetToLocate();

        let pdfjsRequest = { url: documentUrl };

        try {
            if (isStringUUID(documentUrl)) {
                const auth = await LoginService.getAuth();
                if (!$TenantStore?.active) await TenantStore.init();

                pdfDocName = await DocumentStore.getDocumentName(
                    $TenantStore.active.id,
                    documentUrl,
                );

                pdfjsRequest = {
                    url: `${ServiceConsts.API_URL}/tenants/${$TenantStore?.active?.id}/documents/${documentUrl}`,
                    httpHeaders: { Authorization: `Bearer ${auth.token}` },
                };
            } else if (!documentUrl.includes(ServiceConsts.SERVER_URL)) {
                pdfjsRequest = {
                    url: `${ServiceConsts.CLOUD_FLARE_WORKER}${documentUrl}`,
                };
            }

            const loadingTask = pdfjsLib.getDocument(pdfjsRequest);
            pdfDocument = await loadingTask.promise;
            pdfPageCount = pdfDocument.numPages;

            initializePDFViewer();
        } catch (error) {
            console.error("Error loading PDF document:", error);
            pdfNotFound = true;
        }
    };

    const initializePDFViewer = () => {
        userRole = getRole(
            $ActiveTopic,
            $TokenStore.user?.id,
            $TenantStore?.tenants,
            $ClusterStore.clusters,
        );

        pdfViewer = new pdfjsViewer.PDFViewer({
            container: pdfContainer,
            viewer: pdfViewerContainer,
            eventBus: eventBus,
            textLayerMode: 1, // Enable the text layer for selection
            annotationMode: pdfjsLib.AnnotationMode.DISABLE,
            annotationEditorMode: pdfjsLib.AnnotationEditorType.HIGHLIGHT,
            annotationEditorHighlightColors: highlighterColors,
            showHighlightToolbar: userRole >= TopicAccessLevel.EDITOR,
            linkService: linkService, //needed for search
            findController: pdfFindController,
        });

        pdfViewer.setDocument(pdfDocument);
        linkService.setViewer(pdfViewer);
        linkService.setDocument(pdfDocument, null);
    };

    const unsubscribeTopicStore = ActiveTopic.subscribe((topic) => {
        if (topic && topicId !== topic?.id) {
            topicId = topic.id;
            if (pdfViewer) {
                userRole = getRole(
                    $ActiveTopic,
                    $TokenStore.user?.id,
                    $TenantStore?.tenants,
                    $ClusterStore.clusters,
                );

                pdfViewer?._layerProperties?.annotationEditorUIManager?.highlightToolbarVisible(
                    userRole >= TopicAccessLevel.EDITOR,
                );

                removeAllHighlights();

                if ($PreferencesStore?.showAllHighlights)
                    highlightPageSnippets("all");
            }
        }
    });

    const initPDF = async () => {
        if (!viewOnly) {
            // Waiting for extension to load (with all required stores) to open the pdf and unsubscribe
            // from ExtensionStore immediately after the document is loaded
            unsubscribeExtensionStore = ExtensionStore.subscribe(
                (extension) => {
                    if (!extensionLoaded && !extension.loading) {
                        loadPDF(decodeURIComponent($params.pdfLink));
                        extensionLoaded = true;
                    }
                },
            );
        } else {
            let topicId = urlParams.get(ServiceConsts.TOPIC_PARAM_NAME);
            const topic = await TopicService.getSharedTopic(topicId);
            ActiveTopicStore.set({
                topic,
                state: {
                    status: StoreStatus.UP_TO_DATE,
                    id: topic.id,
                },
            });
            userRole = 0;
            loadPDF(decodeURIComponent($params.pdfLink));
        }
    };

    eventBus.on("pagesinit", () => {
        pagesInit = true;
        pdfViewer.currentScaleValue = "page-fit";
    });

    eventBus.on("textlayerrendered", async (event) => {
        if (!viewOnly && $PreferencesStore?.showAllHighlights)
            highlightPageSnippets(event.pageNumber);

        // Since we don't clean the url in view-only mode, we must first verify if
        // the snippet is already highlighted, otherwise it will auto-scroll to that
        // snippet’s page every time a new page is rendered
        if (viewOnly && findEditorBySnippetId(snippetToLocate.id)) return;

        highlightAndLocateSnippet();
    });

    eventBus.on("switchannotationeditorparams", (event) => {
        if (
            event.type ===
                pdfjsLib.AnnotationEditorParamsType.HIGHLIGHT_COLOR &&
            event.editor
        ) {
            event.source.hideDropdown();
            let snippetId = findSnippetIdByEditor(event.editor.id);
            if (snippetId) {
                ActiveTopicStore.updateSnippet({
                    id: snippetId,
                    color: event.value,
                });
            }
        }
    });

    eventBus.on("updateviewarea", (event) => {
        currentPageNumber = pdfViewer.currentPageNumber;
    });

    eventBus.on("updatefindcontrolstate", (event) => {
        let { state, matchesCount } = event;
        noSearchResult = false;
        if (
            state === pdfjsViewer.FindState.FOUND ||
            state === pdfjsViewer.FindState.WRAPPED
        ) {
            searchCurrentPosition = matchesCount.current;
            searchFoundCount = matchesCount.total;
            searchLoading = false;
        } else if (state === pdfjsViewer.FindState.NOT_FOUND) {
            searchFoundCount = 0;
            noSearchResult = true;
            searchLoading = false;
        } else {
            searchLoading = true;
        }
    });

    eventBus.on("updatefindmatchescount", (event) => updateResultsCount(event));

    const debounceSearch = () => {
        typing = true;
        clearTimeout(debounceSearchTimer);
        debounceSearchTimer = setTimeout(async () => {
            search("");
            typing = false;
        }, 300);
    };

    const search = (type, previous = false) => {
        eventBus.dispatch("find", {
            type: type,
            query: searchQuery,
            caseSensitive: false,
            entireWord: false,
            highlightAll: true,
            findPrevious: previous,
            matchDiacritics: true,
        });
    };

    const updateResultsCount = (event) => {
        const { matchesCount } = event;
        searchFoundCount = matchesCount.total;
        searchCurrentPosition = matchesCount.current;
    };

    const selectionChangeHandler = (event) => {
        setSnippetCurrentPage(event);
        checkForMultiPageSelect(event);
    };

    const checkForMultiPageSelect = (event) => {
        const selection = window.getSelection();

        if (!selection || selection.toString().trim() === "") return;

        const range = selection.getRangeAt(0);

        if (range.commonAncestorContainer === pdfViewerContainer) {
            const anchorNode = selection.anchorNode;
            const focusNode = selection.focusNode;
            const anchorOffset = selection.anchorOffset;
            const focusOffset = selection.focusOffset;

            selection.removeAllRanges();

            const newRange = document.createRange();
            const endDiv =
                event.target.activeElement.querySelector(".endOfContent");

            // check text selection direction and adjust the selected text accordingly
            if (
                anchorNode.compareDocumentPosition(focusNode) &
                    Node.DOCUMENT_POSITION_PRECEDING ||
                (anchorNode === focusNode && anchorOffset > focusOffset)
            ) {
                newRange.setStart(
                    event.target.activeElement.querySelector("span"),
                    0,
                );
                newRange.setEnd(range.endContainer, range.endOffset);
            } else {
                newRange.setStart(range.startContainer, range.startOffset);
                newRange.setEndBefore(endDiv);
            }

            selection.addRange(newRange);
        }
    };

    const setSnippetCurrentPage = (event) => {
        const pageNumber = getPageNumberFromTextLayer(
            event.target.activeElement,
        );

        if (
            pageNumber &&
            $ExtensionStore.card.highlight.pageNumber !== pageNumber
        ) {
            $ExtensionStore.card.highlight.pageNumber = pageNumber;
        }
    };

    const getPageNumberFromTextLayer = (textLayerDiv) => {
        for (let pageIndex = 0; pageIndex < pdfPageCount; pageIndex++) {
            const pageView = pdfViewer.getPageView(pageIndex);
            if (pageView.textLayer && pageView.textLayer.div === textLayerDiv) {
                return pageIndex + 1;
            }
        }

        return null;
    };

    const pointerDownHandler = (event) => {
        pointerClicked = true;
        if (
            pdfViewer &&
            pdfViewer._layerProperties?.annotationEditorUIManager
        ) {
            const eventComingFromEditor =
                event.target.closest(".selectedEditor");
            let editorManager =
                pdfViewer._layerProperties.annotationEditorUIManager;
            let selectedEditor = editorManager.firstSelectedEditor;
            if (!eventComingFromEditor && selectedEditor) {
                editorManager.unselect(selectedEditor);
            }

            if (!viewOnly && selectedEditor && eventComingFromEditor) {
                let snippetId = findSnippetIdByEditor(selectedEditor.id);
                if (snippetId) {
                    AppStore.setFocused({ snippetId });
                    ExtensionStore.setOpened(true);
                }
            }
        }
    };

    // Needed to disable highlighter on text selecting so the users can select already highlighted text,
    // without it when we try to select an already highlighted text the software thinks we want to
    // activate the highlight which results in an unexpected behavior
    const pointerUpHandler = (event) => {
        pointerClicked = false;
        if (isSelecting && window.getSelection().toString().length > 0) {
            isSelecting = false;
            pdfViewer.annotationEditorMode = {
                mode: pdfjsLib.AnnotationEditorType.HIGHLIGHT,
            };
        }
    };

    const pointerMoveHandler = (event) => {
        if (
            !isSelecting &&
            pointerClicked &&
            window.getSelection().toString().length > 0
        ) {
            isSelecting = true;
            pdfViewer.annotationEditorMode = {
                mode: pdfjsLib.AnnotationEditorType.NONE,
            };
        }
    };

    const snippetDeletedHandler = (event) => {
        if (pdfViewer && highlightEditors.length) {
            let snippetHighlightEditor = findEditorBySnippetId(event.detail.id);
            if (snippetHighlightEditor) {
                pdfViewer._layerProperties.annotationEditorUIManager.setSelected(
                    snippetHighlightEditor,
                );
                pdfViewer._layerProperties.annotationEditorUIManager.delete();

                highlightEditors.splice(
                    highlightEditors.indexOf(
                        highlightEditors.find(
                            (el) => el.snippetId === event.detail.id,
                        ),
                    ),
                    1,
                );
            }
        }
    };

    const highlightAndLocateSnippet = async () => {
        if (pagesInit) {
            await fetchSnippetToLocate();
            if (snippetToLocate) {
                if (
                    !getRenderedPages().includes(
                        snippetToLocate.highlight.pageNumber,
                    )
                ) {
                    pdfViewer.currentPageNumber =
                        snippetToLocate.highlight.pageNumber;
                } else {
                    await locateSnippet(snippetToLocate.id);
                }
            }
        } else if (toastDiv) {
            showtToastMessage("Document still loading, please wait");
        }
    };

    const getRenderedPages = () => {
        return pdfViewer._pages
            .filter(
                (pageView) =>
                    pageView.renderingState ===
                    pdfjsViewer.RenderingStates.FINISHED,
            )
            .map((pageView) => pageView.id); // Ids are page numbers
    };

    const snippetCreatedHandler = (event) => {
        let snippetId = event.detail.snippet.id;

        if (!$PreferencesStore?.showAllHighlights) {
            removeAllHighlights();
        }

        if (snippetId) {
            pdfViewer._layerProperties.annotationEditorUIManager.currentLayer.setAutoFocusHighlights(
                true,
            );

            let editor =
                pdfViewer._layerProperties.annotationEditorUIManager.highlightSelection();

            highlightEditors.push({
                snippetId: snippetId,
                editor: editor,
            });
        }
    };

    const toggleAllSnippetsHighlight = () => {
        if (pagesInit) {
            removeAllHighlights();
            if ($PreferencesStore?.showAllHighlights) {
                highlightPageSnippets("all");
            }
        }
    };

    const highlightPageSnippets = (pageNumber) => {
        const existingSnippetHighlights = highlightEditors.map(
            (editor) => editor.snippetId,
        );

        const renderedPages = getRenderedPages();

        let snippets = $ActiveTopic?.snippets.filter(
            (snippet) =>
                !snippet.bookmark &&
                !existingSnippetHighlights.includes(snippet.id) &&
                removeUrlParameters(snippet.url) ===
                    removeUrlParameters(window.location.href) &&
                ((pageNumber === "all" &&
                    renderedPages.includes(snippet.highlight?.pageNumber)) ||
                    snippet.highlight?.pageNumber === pageNumber),
        );

        pdfViewer._layerProperties.annotationEditorUIManager?.currentLayer?.setAutoFocusHighlights(
            false,
        );

        snippets?.forEach((snippet) => {
            highlightSnippet(snippet);
        });
    };

    const highlightSnippet = (snippet) => {
        if (findEditorBySnippetId(snippet.id)) return;

        let highlight = snippet.highlight;

        if (highlight) {
            let anchorNode = highlight.anchorNode;
            let focusNode = highlight.focusNode;

            //TODO: Remove this code after the transition period is over and the script is run on production
            // Since our legacy PDF viewer had a slightly different structure, we need to update the anchor and focus nodes
            let snippetCreationDate = new Date(snippet.createdOn);
            let transitionPeriodDate = new Date(1744761600000);
            if (snippetCreationDate < transitionPeriodDate) {
                anchorNode = rebuildSelectionNode(highlight, "anchor");
                focusNode = rebuildSelectionNode(highlight, "focus");
            }

            // The pdf structure has changed once more when we added pdf search, so we need to update the path
            let pdfSearchAddedDate = new Date(1748822400000);
            if (
                snippetCreationDate < pdfSearchAddedDate &&
                snippetCreationDate >= transitionPeriodDate
            ) {
                anchorNode = rebuildSelectionNodeAfterSearchAdded(
                    highlight,
                    "anchor",
                );
                focusNode = rebuildSelectionNodeAfterSearchAdded(
                    highlight,
                    "focus",
                );
            }

            const anchor = getElementAndTextNode(
                anchorNode,
                highlight.anchorOffset,
            );

            const focus = getElementAndTextNode(
                focusNode,
                highlight.focusOffset,
            );

            if (anchor?.node && focus?.node) {
                setSelection(
                    anchor.node,
                    anchor.offset,
                    focus.node,
                    focus.offset,
                );

                const annotationEditor =
                    pdfViewer._layerProperties.annotationEditorUIManager;

                let editor = annotationEditor?.highlightSelection(
                    "",
                    snippet.color,
                );

                if (editor) {
                    annotationEditor?.unselect(editor);

                    highlightEditors.push({
                        snippetId: snippet.id,
                        editor: editor,
                    });

                    window.getSelection().removeAllRanges();

                    return editor;
                }
            }
        }

        return null;
    };

    const rebuildSelectionNodeAfterSearchAdded = (highlight, type) => {
        let selectionNode =
            type === "focus" ? highlight.focusNode : highlight.anchorNode;

        const oldStructure =
            /body:nth-of-type\(1\)>div:nth-of-type\(2\)>div:nth-of-type\(2\)/;

        const newHtmlStructure = `body:nth-of-type(1)>div:nth-of-type(1)>div:nth-of-type(2)>div:nth-of-type(2)`;

        return selectionNode.replace(oldStructure, newHtmlStructure);
    };

    const rebuildSelectionNode = (highlight, type) => {
        let selectionNode =
            type === "focus" ? highlight.focusNode : highlight.anchorNode;

        const oldHtmlPathToPdfWrapper =
            /body:nth-of-type\(1\)>div:nth-of-type\(1\)>div:nth-of-type\(1\)>div:nth-of-type\(2\)/;

        const htmlPathToPdfWrapper = `body:nth-of-type(1)>div:nth-of-type(1)>div:nth-of-type(2)>div:nth-of-type(2)>div:nth-of-type(${highlight.pageNumber})`;

        let rebuiltNode = selectionNode.replace(
            oldHtmlPathToPdfWrapper,
            htmlPathToPdfWrapper,
        );

        // In some documents, the path starts with the 'text-layer' id but since each page has its own
        // text layer and all pages are now rendered we don't use an id on the text layer. We need to replace
        // it with the updated document structure path.
        const textLayerIdRegex = /^#text-layer/;
        const textLayerIdReplacement =
            htmlPathToPdfWrapper + `>div:nth-of-type(2)`;

        if (textLayerIdRegex.test(rebuiltNode)) {
            rebuiltNode = rebuiltNode.replace(
                textLayerIdRegex,
                textLayerIdReplacement,
            );
        }

        // If the node doesn't include a specific span or text node,
        // it's likely referring to the entire page so we set the focusNode
        // to point to the last span in the page.
        const endOfPageRegex = />(span|textNode):nth-of-type\(\d+\)$/;
        if (!endOfPageRegex.test(rebuiltNode)) {
            rebuiltNode = textLayerIdReplacement + ">span:last-of-type";
            const nodeElement = document.querySelector(rebuiltNode);
            if (type === "focus") {
                highlight.focusOffset = nodeElement?.textContent.length || 0;
            } else {
                highlight.anchorOffset = nodeElement?.textContent.length || 0;
            }
        }

        return rebuiltNode;
    };

    const setSelection = (anchorNode, anchorOffset, focusNode, focusOffset) => {
        const selection = window.getSelection();
        selection.removeAllRanges();

        const range = document.createRange();

        // check text selection direction and adjust the selected text accordingly
        if (
            anchorNode.compareDocumentPosition(focusNode) &
                Node.DOCUMENT_POSITION_PRECEDING ||
            (anchorNode === focusNode && anchorOffset > focusOffset)
        ) {
            range.setStart(focusNode, focusOffset);
            range.setEnd(anchorNode, anchorOffset);
        } else {
            range.setStart(anchorNode, anchorOffset);
            range.setEnd(focusNode, focusOffset);
        }

        selection.addRange(range);
    };

    const locateSnippet = async (snippetId) => {
        if (!$ActiveTopicStore) return;

        if (!$PreferencesStore?.showAllHighlights) {
            removeAllHighlights();
        }

        let highlightEditor = findEditorBySnippetId(snippetId);

        if (!highlightEditor) {
            const snippet = await ActiveTopicStore.getSnippet(snippetId);
            if (snippet) highlightEditor = highlightSnippet(snippet);
        }

        if (highlightEditor) {
            highlightEditor.div.scrollIntoView({
                behavior: "instant",
                block: "center",
                inline: "nearest",
            });
            pdfViewer._layerProperties.annotationEditorUIManager.toggleSelected(
                highlightEditor,
            );

            if (highlightSnippetIdInExtension) {
                AppStore.setFocused({
                    snippetId: highlightSnippetIdInExtension,
                });
                highlightSnippetIdInExtension = null;
            }
        }

        snippetToLocate = null;

        if (!viewOnly) {
            clearUrlByMarker("#");
        }
    };

    const removeAllHighlights = () => {
        if (pdfViewer && highlightEditors.length) {
            highlightEditors
                .filter((highlightEditor) => highlightEditor.editor)
                .forEach((highlightEditor) => {
                    pdfViewer._layerProperties.annotationEditorUIManager.setSelected(
                        highlightEditor.editor,
                    );
                    pdfViewer._layerProperties.annotationEditorUIManager.delete();
                });
            highlightEditors = [];
        }
    };

    const findEditorBySnippetId = (snippetId) => {
        return highlightEditors.find(
            (highlightEditor) => highlightEditor.snippetId === snippetId,
        )?.editor;
    };

    const findSnippetIdByEditor = (editorId) => {
        return highlightEditors.find(
            (highlightEditor) => highlightEditor.editor?.id === editorId,
        )?.snippetId;
    };

    const zoom = (out) => {
        if (out) {
            if (scale <= 0.5) return;
            scale -= 0.1;
        } else {
            scale += 0.1;
        }
        pdfViewer.currentScaleValue = scale;
    };

    const changePDFPage = (event) => {
        if (!currentPageNumber) return;

        if (event.key === "ArrowUp" && currentPageNumber < pdfPageCount) {
            event.preventDefault();
            currentPageNumber = currentPageNumber + 1;
        } else if (event.key === "ArrowDown" && currentPageNumber > 1) {
            event.preventDefault();
            currentPageNumber = currentPageNumber - 1;
        }

        if (
            !isPositiveWholeNumber(currentPageNumber) ||
            currentPageNumber < 1 ||
            currentPageNumber > pdfPageCount
        ) {
            currentPageNumber = pdfViewer.currentPageNumber;
            return;
        }

        if (event.type === "keydown" && event.key === "Enter") {
            event.target.blur();
            return;
        }

        if (event.type === "blur") {
            pdfViewer.scrollPageIntoView({
                pageNumber: parseInt(currentPageNumber),
            });
        }
    };
</script>

<svelte:head>
    <title>{pdfDocName || "PDF Highlighter"}</title>
    <link rel="stylesheet" href="/pdfjs/pdf_viewer.css" />
</svelte:head>

<div class="flex flex-col">
    <div
        class="pdf-viewer-toolbar"
        class:extension-panel-closed={!$ExtensionStore.opened}
        class:extension-panel-right={$PreferencesStore.positionPanelRight}
    >
        <div class="pdf-viewer-toolbar__header">
            {#if !viewOnly}
                <ToolbarBackButton />
            {/if}

            <svg
                class="pdf-viewer-toolbar__icon hidden sm:block"
                stroke="currentColor"
                viewBox="0 0 90 90"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M33.9719 41.1031C34.7811 41.1031 35.4787 41.3413 36.0647 41.8176C36.6506 42.2659 36.9436 42.8544 36.9436 43.5829C36.9436 43.9472 36.846 44.4095 36.6506 44.9699C36.4832 45.5303 36.26 46.0347 35.981 46.483C34.9764 48.1923 33.7068 49.4532 32.1721 50.2658C30.6375 51.0784 28.74 51.4846 26.4798 51.4846C23.8848 51.4846 21.6246 50.8542 19.6993 49.5933C17.774 48.3324 16.2951 46.5391 15.2626 44.2134C14.2581 41.8877 13.7559 39.1837 13.7559 36.1015C13.7559 33.0472 14.2581 30.3993 15.2626 28.1577C16.2672 25.9161 17.7182 24.2068 19.6156 23.03C21.5409 21.8251 23.843 21.2226 26.5217 21.2226C28.4749 21.2226 30.2468 21.5449 31.8373 22.1893C33.4278 22.8058 34.6695 23.6744 35.5624 24.7952C36.4832 25.888 36.9436 27.1209 36.9436 28.4939C36.9436 29.7268 36.5669 30.7356 35.8135 31.5201C35.0881 32.3047 34.1393 32.697 32.9674 32.697C31.7675 32.697 30.8049 32.3748 30.0794 31.7303C29.3539 31.0578 28.9911 30.1752 28.9911 29.0824C28.9911 28.4939 29.1028 27.9335 29.326 27.4011C28.7121 27.0089 27.8192 26.8127 26.6473 26.8127C24.4987 26.8127 22.9501 27.5552 22.0013 29.0403C21.0526 30.4974 20.5783 32.8091 20.5783 35.9754C20.5783 39.2818 21.0945 41.7616 22.1269 43.4148C23.1593 45.068 24.7638 45.8946 26.9402 45.8946C27.9727 45.8946 28.8377 45.6704 29.5353 45.2221C30.2329 44.7457 30.8328 44.0172 31.335 43.0365C31.6978 42.28 32.0605 41.7756 32.4233 41.5234C32.8139 41.2432 33.3301 41.1031 33.9719 41.1031Z"
                />
                <path
                    d="M77.3862 35.3711C77.9982 35.3711 78.485 35.5235 78.8466 35.8281C79.2082 36.1051 79.3891 36.5621 79.3891 37.1992V39.0273C79.3891 39.6643 79.2082 40.1352 78.8466 40.4399C78.485 40.7445 77.9982 40.8969 77.3862 40.8969H76.7604L70.8771 62.9999C70.7658 63.4431 70.5155 63.8032 70.126 64.0802C69.7366 64.3294 69.3054 64.4541 68.8325 64.4541H65.578C65.0773 64.4541 64.6322 64.3294 64.2427 64.0802C63.8811 63.8032 63.6308 63.4431 63.4917 62.9999L58.7767 48.1676L54.0618 62.9999C53.9227 63.4431 53.6584 63.8032 53.269 64.0802C52.8795 64.3294 52.4345 64.4541 51.9338 64.4541H48.6375C48.1646 64.4541 47.7334 64.3294 47.344 64.0802C46.9545 63.8032 46.7042 63.4431 46.5929 62.9999L40.7097 40.8969H40.0838C39.4996 40.8969 39.0267 40.7445 38.6651 40.4399C38.3035 40.1352 38.1227 39.6643 38.1227 39.0273V37.1992C38.1227 36.5621 38.3035 36.1051 38.6651 35.8281C39.0267 35.5235 39.4996 35.3711 40.0838 35.3711H48.1368C48.7209 35.3711 49.1938 35.5235 49.5554 35.8281C49.917 36.1051 50.0979 36.5621 50.0979 37.1992V39.0273C50.0979 39.6643 49.917 40.1352 49.5554 40.4399C49.1938 40.7445 48.7209 40.8969 48.1368 40.8969H47.5109L51.141 55.106L55.6473 41.0215C55.7864 40.5784 56.0229 40.2321 56.3567 39.9828C56.6905 39.7059 57.0799 39.5674 57.525 39.5674H61.2385C61.6558 39.5674 62.0313 39.7059 62.3651 39.9828C62.6989 40.2321 62.9354 40.5784 63.0744 41.0215L67.5391 54.9398L71.1274 40.8969H70.5016C69.8896 40.8969 69.4028 40.7445 69.0412 40.4399C68.6795 40.1352 68.4987 39.6643 68.4987 39.0273V37.1992C68.4987 36.5621 68.6795 36.1051 69.0412 35.8281C69.4028 35.5235 69.8896 35.3711 70.5016 35.3711H77.3862Z"
                />
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M39.6223 21.2995C38.5944 21.4756 37.573 20.9557 37.1105 20.021L36.9292 19.6545C36.5997 18.9883 36.6034 18.2058 36.9394 17.5429C37.2754 16.8799 37.9042 16.4142 38.6363 16.286C40.7566 15.915 42.9511 15.7205 45.1969 15.7205C55.6407 15.7205 65.021 19.9327 70.9483 26.5968C71.5317 27.2528 71.7024 28.1784 71.3915 28.9993C71.0805 29.8202 70.3393 30.4004 69.4678 30.5052L68.9891 30.5628C68.2309 30.6539 67.4755 30.3722 66.9621 29.8069C62.0739 24.4247 54.2151 20.8296 45.1969 20.8296C43.2822 20.8296 41.418 20.9919 39.6223 21.2995Z"
                />
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M29.0445 64.5747C29.3227 63.4595 30.3645 62.7073 31.5106 62.7942L32.7521 62.8882C32.9886 62.9061 33.2209 62.9596 33.4414 63.0468C35.2503 63.7624 37.1744 64.3192 39.188 64.6927C39.3665 64.7258 39.5405 64.7794 39.7067 64.8525L41.1437 65.484C42.222 65.9579 42.7852 67.1548 42.4631 68.2877C42.141 69.4206 41.0323 70.1421 39.866 69.9778C36.5065 69.5046 33.3168 68.5909 30.3852 67.305C29.3326 66.8433 28.7663 65.6899 29.0445 64.5747Z"
                />
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M20.9882 55.8732C22.2544 56.1778 23.034 57.4512 22.7294 58.7174C21.9399 61.9996 21.1008 64.5684 20.4944 66.4251C20.431 66.6191 20.3702 66.8052 20.3123 66.9837C21.3605 66.5797 22.6163 66.059 24.175 65.4128C25.8212 64.7302 27.8053 63.9076 30.2399 62.9343C31.4491 62.4508 32.8214 63.0392 33.3048 64.2485C33.7883 65.4577 33.1999 66.83 31.9906 67.3134C29.5657 68.2829 27.4343 69.1712 25.6589 69.9111C24.1522 70.539 22.9018 71.0601 21.9458 71.4334C20.8708 71.8531 19.8941 72.1998 19.0863 72.3636C18.6821 72.4456 18.1713 72.5146 17.635 72.4551C17.0658 72.392 16.3274 72.1641 15.7349 71.5229C15.161 70.9018 14.9928 70.1813 14.9493 69.6781C14.9066 69.1839 14.9663 68.7104 15.0354 68.3331C15.0683 68.1534 15.1095 67.9647 15.1574 67.7688C15.3096 67.1255 15.537 66.4014 15.7763 65.6612C15.8594 65.4044 15.9471 65.1361 16.0388 64.8556C16.6409 63.0141 17.4137 60.6505 18.144 57.6144C18.4486 56.3482 19.722 55.5686 20.9882 55.8732Z"
                />
            </svg>

            <a
                href={viewOnly ? $url("/") : $url("/pdf")}
                class="pdf-viewer-toolbar__heading"
            >
                PDF Highlighter
            </a>
        </div>

        <div
            class="pdf-viewer-toolbar__buttons"
            class:pdf-viewer-toolbar__buttons--hide={pdfNotFound || !pagesInit}
            class:pdf-viewer-toolbar__buttons--view-only={viewOnly}
        >
            <div class="page-navigation">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="input-icons"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                    />
                </svg>

                <input
                    bind:value={currentPageNumber}
                    on:blur={changePDFPage}
                    on:keydown={changePDFPage}
                    class="pdf-viewer-page-number"
                />
                <span class="pdf-viewer-page-count">/ {pdfPageCount}</span>
            </div>

            <div
                class="toolbar-wrapper"
                class:no-result-warning={noSearchResult}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="input-icons search-input-icon"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Zm3.75 11.625a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                </svg>

                <input
                    class:bg-red-200={noSearchResult}
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    on:keydown={(e) => e.key === "Enter" && search("again")}
                    type="text"
                    placeholder="Search document"
                    class="search-input"
                />

                <span class="search-spinner" class:hidden={!searchLoading}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-5 animate-spin"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                    </svg>
                </span>

                <div
                    class="search-results"
                    class:too-many-matches={searchFoundCount > 999}
                    class:invisible={searchQuery === "" ||
                        typing ||
                        searchLoading}
                >
                    {#if noSearchResult}
                        Not found
                    {:else if searchFoundCount > 999}
                        1000+ finds
                    {:else}
                        {searchCurrentPosition} / {searchFoundCount}
                    {/if}
                </div>
                <button
                    on:click={() => search("again", true)}
                    disabled={searchFoundCount < 1}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-5"
                        class:text-gray-400={searchFoundCount < 1}
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m4.5 15.75 7.5-7.5 7.5 7.5"
                        />
                    </svg>
                </button>
                <button
                    on:click={() => search("again")}
                    disabled={searchFoundCount < 1}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-5"
                        class:text-gray-400={searchFoundCount < 1}
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m19.5 8.25-7.5 7.5-7.5-7.5"
                        />
                    </svg>
                </button>
            </div>

            <button
                on:click={() => zoom(true)}
                class="pdf-viewer-toolbar__button"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-7"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6"
                    />
                </svg>
            </button>

            <button
                on:click={() => zoom(false)}
                class="pdf-viewer-toolbar__button"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-7"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
                    />
                </svg>
            </button>
        </div>
    </div>
    <div
        class="viewer-container"
        class:extension-panel-right={$PreferencesStore.positionPanelRight}
        class:extension-panel-closed={!$ExtensionStore.opened}
        class:extension-loading={!pagesInit}
        bind:this={pdfContainer}
    >
        {#if pdfNotFound}
            <div
                class="not-found"
                class:extension-panel-right={$PreferencesStore.positionPanelRight}
                class:extension-panel-closed={!$ExtensionStore.opened}
            >
                <img class="not-found__error-img" src={ErrorImg} alt="Error" />
                <div class="not-found__text">
                    <span>We couldn't find this document 😔</span>
                </div>
            </div>
        {:else}
            <div
                class="pdfViewer"
                class:invisible={!pagesInit}
                bind:this={pdfViewerContainer}
            />
        {/if}
    </div>
</div>

{#if !pagesInit && !pdfNotFound}
    <div
        class="loading-animation-wrapper"
        class:extension-panel-right={$PreferencesStore.positionPanelRight}
        class:extension-panel-closed={!$ExtensionStore.opened}
        class:view-only-mode={viewOnly}
    >
        <LoadingMessage>Loading PDF document...</LoadingMessage>
    </div>
{/if}

{#if !viewOnly}
    <Extension
        on:snippetCreated={snippetCreatedHandler}
        on:snippetDeleted={snippetDeletedHandler}
    />
{/if}
<div
    id="toast"
    class="toast-wrapper"
    class:extension-panel-right={$PreferencesStore.positionPanelRight}
    class:extension-panel-closed={!$ExtensionStore.opened}
    bind:this={toastDiv}
></div>

<style>
    /* purgecss start ignore */

    :global(.toggle-button::before) {
        content: none !important;
    }

    :global(.colorPicker .hidden) {
        display: none !important;
    }

    :global(.markedContent),
    :global(.annotationLayer),
    :global(.annotationEditorLayer),
    :global(.textLayer) {
        user-select: none;
    }

    :global(.textLayer span) {
        user-select: text;
    }

    :global(.annotationEditorLayer),
    :global(.textLayer:focus-visible),
    :global(.page:focus-visible) {
        border-color: transparent !important;
        outline-color: transparent !important;
    }

    :global(.page) {
        border: none !important;
        margin: 10px auto !important;
        outline-color: transparent !important;
    }

    :global(body > .toast-wrapper > .toast) {
        @apply bg-gray-700 text-white !important;
    }

    :global(.loading-animation-wrapper .loading-gif) {
        z-index: 10;
    }

    /* purgecss end ignore */

    .loading-animation-wrapper {
        @apply flex h-full min-h-screen w-full min-w-full flex-col items-center bg-white;
    }

    .pdfViewer {
        @apply z-0;
    }

    .viewer-container {
        @apply absolute bottom-0 top-[90px] z-0 flex w-full flex-col overflow-auto bg-gray-200 text-center sm:pl-[350px] md:top-[49px];
    }

    .viewer-container::-webkit-scrollbar-thumb {
        background: #c7c8cb !important;
    }

    .viewer-container::-webkit-scrollbar-track {
        background: #f9fbfd;
    }

    .toast-wrapper {
        @apply absolute left-0 right-0 z-50 flex h-0 w-full justify-center sm:pl-[350px];
    }

    :global(body > .toast-wrapper > .toast) {
        @apply bg-gray-700 text-white !important;
    }

    .pdf-viewer-toolbar {
        @apply z-10 flex select-none flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-solid border-gray-300 bg-gray-200 px-4 py-1 pr-2 shadow-md sm:pl-[358px];
    }

    .pdf-viewer-toolbar.extension-panel-right {
        @apply sm:pl-2 sm:pr-[358px] !important;
    }

    .pdf-viewer-toolbar.extension-panel-closed.extension-panel-right {
        @apply sm:pl-2 sm:pr-12 !important;
    }

    .pdf-viewer-toolbar.extension-panel-closed {
        @apply sm:w-full sm:pl-14 sm:pr-2;
    }

    .pdf-viewer-page-number {
        @apply w-8 text-right leading-tight focus:outline-none;
    }

    .pdf-viewer-toolbar__header {
        @apply flex min-h-10 flex-row flex-wrap items-center gap-2 md:justify-center;
    }

    .pdf-viewer-toolbar__icon {
        @apply h-8 w-8 fill-gray-400 text-gray-400;
    }

    .pdf-viewer-toolbar__heading {
        @apply hidden select-none flex-row font-sans text-lg font-medium leading-none text-gray-400 sm:flex;
    }

    .pdf-viewer-toolbar__button {
        @apply flex h-8 w-8 items-center justify-center rounded-full fill-gray-400 text-gray-400 transition-colors duration-200 ease-in-out hover:bg-gray-300 hover:text-black;
    }

    .pdf-viewer-toolbar__button.active {
        @apply bg-gray-300 text-black;
    }

    .pdf-viewer-toolbar__button svg {
        @apply h-5 w-5;
    }

    .pdf-viewer-toolbar__buttons {
        @apply ml-0 flex flex-row flex-nowrap items-center gap-2 overflow-hidden py-1 md:ml-auto;
    }

    .pdf-viewer-toolbar__buttons--view-only {
        @apply pr-0;
    }

    .pdf-viewer-toolbar__buttons--hide {
        @apply hidden;
    }

    .not-found {
        @apply relative flex h-screen w-full flex-col items-center justify-center bg-gray-100;
    }

    .not-found__error-img {
        @apply w-96 max-w-full;
    }

    .not-found__text {
        @apply mt-2 text-base;
    }

    .viewer-container.extension-panel-right {
        @apply sm:w-[calc(100%-350px)] sm:pl-0 !important;
    }

    .not-found.extension-panel-closed,
    .loading-animation-wrapper.view-only-mode,
    .viewer-container.extension-panel-closed {
        @apply pl-0 sm:w-full sm:pr-0 !important;
    }

    .toast-wrapper.extension-panel-closed {
        @apply sm:pl-0 sm:pr-0 !important;
    }

    .loading-animation-wrapper {
        @apply sm:pl-[350px] !important;
    }

    .loading-animation-wrapper.extension-panel-right {
        @apply sm:pl-0 sm:pr-[350px] !important;
    }

    .loading-animation-wrapper.extension-panel-closed {
        @apply sm:pl-0 sm:pr-0 !important;
    }

    .toolbar-wrapper {
        @apply relative flex min-w-[280px] shrink flex-row flex-nowrap items-center justify-around gap-1 gap-y-0 rounded-2xl border border-solid border-transparent bg-white px-2 sm:min-w-[380px] sm:gap-2 sm:px-3;
    }

    .no-result-warning {
        @apply border border-solid border-red-700 bg-red-200;
    }

    .input-icons {
        @apply size-5 text-gray-400;
    }

    .search-input-icon {
        @apply hidden sm:block;
    }

    .pdf-viewer-page-count {
        @apply leading-tight text-gray-400;
    }

    .page-navigation {
        @apply absolute left-1/2 top-2.5 flex -translate-x-1/2 transform flex-row items-center gap-1 rounded-2xl bg-white px-3 py-1 sm:static sm:translate-x-0;
    }

    .search-spinner {
        @apply absolute right-20 text-gray-400;
    }

    .search-input {
        @apply min-w-0 flex-shrink py-1 leading-tight focus:outline-none;
    }

    .search-results {
        @apply min-w-20 text-right text-gray-400;
    }

    .too-many-matches {
        @apply min-w-24 sm:min-w-20 !important;
    }

    .extension-loading {
        @apply overflow-hidden bg-white !important;
    }
</style>
